import React, { useEffect, useState, useRef } from "react";
import { AdminShell } from "./AdminShell";
import { authApi } from "../authApi";
import { API } from "../api";
import { ACQ_ORIGINS, originMeta, OriginBadge } from "../acquereurOrigins";
import { SoundWave } from "../SoundWave";
import { ShieldCheck, Mail, Phone, MapPin, Loader2, Home, ExternalLink, Sparkles, Upload, Mic, Square, X, Check, AlertTriangle } from "lucide-react";

function VoiceImportModal({ onClose, onDone }) {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const [invite, setInvite] = useState("none");
  const [err, setErr] = useState("");
  const mrRef = useRef(null); const chunksRef = useRef([]); const timerRef = useRef(null);

  const start = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 25 * 1024 * 1024) { setErr("Enregistrement trop long (max ~25 Mo)."); return; }
        setBusy(true);
        try { const fd = new FormData(); fd.append("audio", blob, "acq.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", body: fd, credentials: "include" });
          const d = await res.json(); setTranscript((t) => (t ? t + " " : "") + (d.text || ""));
        } catch (_) { setErr("Transcription impossible."); }
        setBusy(false);
      };
      mr.start(); mrRef.current = mr; setRecording(true); setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch (_) { setErr("Micro indisponible."); }
  };
  const stop = () => { if (mrRef.current && recording) { mrRef.current.stop(); setRecording(false); } };
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const analyze = async () => {
    setBusy(true); setErr("");
    try { const d = await authApi.voiceParseAcq(transcript); setRows((d.acquereurs || []).map((a) => ({ ...a, _action: a._duplicate ? "update" : "create" }))); }
    catch (e) { setErr(e.message || "Analyse impossible."); }
    setBusy(false);
  };
  const upd = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const create = async () => {
    setBusy(true); setErr("");
    try { const d = await authApi.bulkCreateAcq(rows, invite);
      onDone(`${d.created} créé(s)${d.updated ? `, ${d.updated} mis à jour` : ""}${d.skipped ? `, ${d.skipped} ignoré(s)` : ""}.`); }
    catch (e) { setErr(e.message || "Création impossible."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm overflow-y-auto py-8 px-3" data-testid="voice-import-modal">
      <div className="max-w-3xl mx-auto bg-[#0d0d10] border border-white/10 p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-light text-white flex items-center gap-2"><Mic className="w-5 h-5 text-[#C19A6B]" /> Importer des acquéreurs par la voix</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!rows ? (
          <>
            <p className="text-sm text-zinc-400">Dictez vos contacts à la suite : « Monsieur Jean Dupont, 06…, email…, cherche une maison à Chantilly, budget 450 000 €, 3 chambres avec jardin. Ensuite Madame… ». Vous pouvez enregistrer plusieurs fois.</p>
            <div className="flex items-center gap-3">
              {!recording ? (
                <button onClick={start} disabled={busy} className="inline-flex items-center gap-2 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-50 text-sm uppercase tracking-wider px-4 py-2.5" data-testid="vi-rec-start">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Transcription…</> : <><Mic className="w-4 h-4" /> Enregistrer</>}</button>
              ) : (
                <button onClick={stop} className="inline-flex items-center gap-2 bg-red-500 text-white text-sm uppercase tracking-wider px-4 py-2.5" data-testid="vi-rec-stop"><SoundWave className="text-white" /> Arrêter · {fmt(secs)}</button>
              )}
            </div>
            <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} placeholder="La retranscription apparaît ici — vous pouvez aussi taper directement…" className="w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-zinc-200 min-h-[120px] outline-none focus:border-[#C19A6B]" data-testid="vi-transcript" />
            {err && <p className="text-sm text-red-400">{err}</p>}
            <button onClick={analyze} disabled={busy || transcript.trim().length < 15} className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider py-3" data-testid="vi-analyze">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse IA…</> : <><Sparkles className="w-4 h-4" /> Analyser & découper</>}</button>
          </>
        ) : (
          <>
            <p className="text-sm text-zinc-300">{rows.length} acquéreur(s) détecté(s). Vérifiez et corrigez avant création.</p>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {rows.map((a, i) => (
                <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`vi-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-medium">{(a.first_name || "") + " " + (a.last_name || "") || "Sans nom"}</span>
                    {a._duplicate && <span className="inline-flex items-center gap-1 text-[10px] text-amber-400"><AlertTriangle className="w-3 h-3" /> Doublon détecté</span>}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[["first_name", "Prénom"], ["last_name", "Nom"], ["phone", "Téléphone"], ["email", "Email"], ["city", "Ville"], ["budget_max", "Budget max"]].map(([k, lbl]) => (
                      <input key={k} value={a[k] || ""} onChange={(e) => upd(i, k, e.target.value)} placeholder={lbl} className="bg-white/5 border border-white/15 px-2 py-1.5 text-xs text-white outline-none focus:border-[#C19A6B]" />
                    ))}
                  </div>
                  {(a._missing || []).length > 0 && <p className="text-[10px] text-zinc-500">Manquant : {(a._missing).join(", ")}</p>}
                  <select value={a._action} onChange={(e) => upd(i, "_action", e.target.value)} className="bg-white/5 border border-white/15 px-2 py-1.5 text-xs text-white outline-none">
                    <option value="create" className="bg-[#0d0d10]">Créer une nouvelle fiche</option>
                    {a._duplicate && <option value="update" className="bg-[#0d0d10]">Mettre à jour l'existante</option>}
                    {a._duplicate && <option value="merge" className="bg-[#0d0d10]">Fusionner (compléter l'existante)</option>}
                    <option value="skip" className="bg-[#0d0d10]">Ignorer</option>
                  </select>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs text-zinc-400">Invitation OFF MARKETTE :</label>
              <select value={invite} onChange={(e) => setInvite(e.target.value)} className="bg-white/5 border border-white/15 px-2 py-1.5 text-xs text-white outline-none">
                <option value="none" className="bg-[#0d0d10]">Enregistrer sans inviter</option>
                <option value="now" className="bg-[#0d0d10]">Inviter maintenant</option>
                <option value="later" className="bg-[#0d0d10]">Programmer plus tard</option>
              </select>
            </div>
            {err && <p className="text-sm text-red-400">{err}</p>}
            <div className="flex gap-2">
              <button onClick={() => setRows(null)} className="flex-1 border border-white/15 text-zinc-300 text-sm uppercase tracking-wider py-2.5">Retour</button>
              <button onClick={create} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider py-2.5" data-testid="vi-create"><Check className="w-4 h-4" /> {busy ? "Création…" : "Créer les fiches"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AcqCard({ a }) {
  const [matches, setMatches] = useState(null); // null=not loaded, []=loaded
  const [loading, setLoading] = useState(false);
  const loadMatches = async () => {
    setLoading(true);
    try { const r = await authApi.adminAcquereurMatches(a.id); setMatches(r.matches || []); } catch (_) { setMatches([]); }
    setLoading(false);
  };
  return (
    <div className="border border-white/10 p-5 bg-[#0d0d10]" data-testid={`acquereur-${a.id}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white font-medium flex items-center gap-2">
          {a.anonymous && <ShieldCheck className="w-4 h-4 text-[#C19A6B]" title="Anonyme" />}
          {a.display_name || a.name || "—"}
        </h3>
        <span className="text-[10px] uppercase tracking-wider bg-[#C19A6B]/20 text-[#C19A6B] px-2 py-1">{a.status || "nouveau"}</span>
      </div>
      <div className="mb-3"><OriginBadge origin={a.origin} />{a.origin_channel && <span className="text-[10px] text-zinc-500 ml-2">via {a.origin_channel}</span>}</div>
      <div className="flex flex-wrap gap-3 text-xs text-zinc-400 mb-3">
        {a.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {a.email}</span>}
        {a.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {a.phone}</span>}
      </div>
      {a.summary && <p className="text-sm text-zinc-300 mb-3">{a.summary}</p>}
      <div className="flex flex-wrap gap-2 text-[11px]">
        {a.budget && <span className="border border-white/15 px-2 py-1 text-zinc-300">💶 {a.budget}</span>}
        {(a.type_bien || []).map((t, i) => <span key={i} className="border border-white/15 px-2 py-1 text-zinc-300">{t}</span>)}
        {(a.secteurs || []).map((s, i) => <span key={i} className="border border-[#C19A6B]/30 px-2 py-1 text-[#C19A6B] inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s}</span>)}
      </div>
      {(a.criteres || []).length > 0 && <p className="text-[11px] text-zinc-500 mt-3">Critères : {(a.criteres || []).join(" · ")}</p>}
      {a.urgence && <p className="text-[11px] text-amber-400 mt-1">Urgence : {a.urgence}</p>}

      <button onClick={loadMatches} disabled={loading} data-testid={`acq-match-btn-${a.id}`} className="mt-4 inline-flex items-center gap-2 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-60 text-xs uppercase tracking-wider px-4 py-2 transition-colors">
        {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Recherche…</> : <><Sparkles className="w-3.5 h-3.5" /> Biens correspondants</>}
      </button>

      {matches !== null && (
        <div className="mt-3 space-y-2" data-testid={`acq-matches-${a.id}`}>
          {matches.length === 0 ? (
            <p className="text-xs text-zinc-500">Aucun bien ne correspond pour le moment.</p>
          ) : matches.map((m) => (
            <a key={m.slug} href={`/bien/${m.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 border border-white/10 p-2 hover:border-[#C19A6B]/40 transition-colors group">
              <div className="w-12 h-12 bg-black/40 overflow-hidden shrink-0">{(m.cover || m.images?.[0]) && <img src={m.cover || m.images[0]} alt="" className="w-full h-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate flex items-center gap-1"><Home className="w-3 h-3 text-[#C19A6B]" /> {m.title}</p>
                <p className="text-[11px] text-zinc-500 truncate">{m.city}{m.price ? ` · ${Number(m.price).toLocaleString("fr-FR")} €` : ""}{m.owner?.display_name ? ` · ${m.owner.display_name}` : ""}</p>
                {(m.match_reasons || []).length > 0 && (
                  <p className="text-[10px] text-[#C19A6B] truncate mt-0.5">{m.match_reasons.join(" · ")}</p>
                )}
              </div>
              <span className="text-[10px] uppercase tracking-wider bg-[#C19A6B] text-white px-2 py-0.5 shrink-0">{m.match_score}%</span>
              <ExternalLink className="w-3.5 h-3.5 text-zinc-500 group-hover:text-[#C19A6B]" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function MatchingConfig() {
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { authApi.getMatchingConfig().then(setCfg).catch(() => {}); }, []);
  if (!cfg) return null;
  const save = async () => {
    const r = await authApi.setMatchingConfig(cfg);
    setCfg(r); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div className="border border-white/10 bg-[#0d0d10] p-4 mb-5 flex flex-wrap items-end gap-4" data-testid="matching-config">
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Seuil d'affichage (%)</label>
        <input type="number" min="10" max="100" value={cfg.display_threshold} onChange={(e) => setCfg({ ...cfg, display_threshold: e.target.value })} data-testid="cfg-display" className="w-24 bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Seuil d'alerte e-mail (%)</label>
        <input type="number" min="10" max="100" value={cfg.alert_threshold} onChange={(e) => setCfg({ ...cfg, alert_threshold: e.target.value })} data-testid="cfg-alert" className="w-24 bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
      </div>
      <button onClick={save} data-testid="cfg-save" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-medium uppercase tracking-wider px-4 py-2.5 transition-colors">{saved ? "Enregistré ✓" : "Enregistrer les seuils"}</button>
      <p className="text-[11px] text-zinc-500 flex-1 min-w-[200px]">Le seuil d'affichage filtre les biens montrés aux acquéreurs ; le seuil d'alerte déclenche l'e-mail (plus élevé = plus strict).</p>
    </div>
  );
}

export default function AdminAcquereurs({ embedded } = {}) {
  const [items, setItems] = useState(null);
  const [originFilter, setOriginFilter] = useState("all");
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [voiceOpen, setVoiceOpen] = useState(false);
  const fileRef = useRef();
  const reload = () => authApi.adminAcquereurs().then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportMsg("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/admin/acquereurs/import`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setImportMsg(`${d.imported} acquéreur(s) importé(s)${d.skipped ? `, ${d.skipped} ignoré(s) (doublons/vides)` : ""}.`);
      reload();
    } catch (_) { setImportMsg("Import impossible. Vérifiez le fichier (CSV ou Excel .xlsx)."); }
    setImporting(false);
    e.target.value = "";
  };
  const counts = React.useMemo(() => {
    const c = {};
    (items || []).forEach((a) => { const o = a.origin || "spontane"; c[o] = (c[o] || 0) + 1; });
    return c;
  }, [items]);
  const filtered = (items || []).filter((a) => originFilter === "all" || (a.origin || "spontane") === originFilter);
  const presentOrigins = Object.keys(ACQ_ORIGINS).filter((k) => counts[k]);
  return (
    <AdminShell embedded={embedded} title="Acquéreurs">
      <div className="border border-[#C19A6B]/30 bg-gradient-to-r from-[#C19A6B]/10 to-transparent p-4 mb-5 flex flex-wrap items-center justify-between gap-3" data-testid="acq-import-block">
        <div>
          <h3 className="text-white text-sm font-medium flex items-center gap-2"><Upload className="w-4 h-4 text-[#C19A6B]" /> Importer mon fichier historique</h3>
          <p className="text-xs text-zinc-400 mt-1">Excel (.xlsx) ou CSV. Colonnes reconnues : nom, prénom, email, téléphone, budget, ville/secteur, type, notes. Marqués « 🏆 Mon fichier historique ».</p>
          {importMsg && <p className="text-xs text-[#C19A6B] mt-2" data-testid="acq-import-msg">{importMsg}</p>}
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={importing} className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider px-5 py-2.5 transition-colors" data-testid="acq-import-btn">
          {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Import…</> : <><Upload className="w-4 h-4" /> Choisir un fichier</>}
        </button>
        <button onClick={() => setVoiceOpen(true)} className="inline-flex items-center gap-2 border border-[#A855F7]/60 text-[#c58bff] hover:bg-[#A855F7]/10 text-sm font-semibold uppercase tracking-wider px-5 py-2.5 transition-colors group" data-testid="acq-voice-btn">
          <SoundWave active={false} bars={5} className="text-[#c58bff] group-hover:hidden" /><span className="hidden group-hover:inline-flex"><SoundWave bars={5} className="text-[#c58bff]" /></span> Importer par la voix
        </button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onImport} className="hidden" />
      </div>
      <MatchingConfig />
      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="acq-origin-filter">
          <button onClick={() => setOriginFilter("all")} className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${originFilter === "all" ? "bg-white text-black border-white" : "border-white/20 text-zinc-400 hover:text-white"}`}>Tous ({items.length})</button>
          {presentOrigins.map((k) => {
            const m = originMeta(k);
            const active = originFilter === k;
            return (
              <button key={k} onClick={() => setOriginFilter(k)} data-testid={`acq-origin-filter-${k}`} className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors" style={active ? { backgroundColor: m.color, borderColor: m.color, color: "#000" } : { borderColor: m.color + "55", color: m.color }}>
                {m.emoji} {m.label} ({counts[k]})
              </button>
            );
          })}
        </div>
      )}
      {items === null ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-zinc-500">Aucun acquéreur{originFilter !== "all" ? " pour cette origine" : ""} pour l'instant.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="admin-acquereurs">
          {filtered.map((a) => <AcqCard key={a.id} a={a} />)}
        </div>
      )}
      {voiceOpen && <VoiceImportModal onClose={() => setVoiceOpen(false)} onDone={(msg) => { setVoiceOpen(false); setImportMsg(msg); reload(); }} />}
    </AdminShell>
  );
}
